import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';
import { categoryTone } from '../../tickets/lib/data';
import '../../tickets/tickets.css';

/** The query key of a conversation's linked tickets; invalidate it after creating one. */
export const linkedTicketKey = (conversationId: string) => ['tickets', 'by-conversation', conversationId];

/**
 * The ticket this conversation is linked to (7 October 2026): the newest one still being worked, or else the
 * newest. Anything that goes wrong (no tickets service yet, a refusal) answers null, and the chip hides itself:
 * the thread header looks exactly as it did.
 */
export function useLinkedTicket(conversationId: string): tickets.TicketSummary | null {
  const q = useQuery({
    queryKey: linkedTicketKey(conversationId),
    queryFn: () => tickets.listTickets({ conversation_id: conversationId, limit: 5 }).catch(() => null),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false
  });
  const rows = q.data?.data ?? [];
  if (!rows.length) return null;
  const byNewest = [...rows].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  return byNewest.find((x) => x.status.category !== 'closed' && x.status.category !== 'solved') ?? byNewest[0] ?? null;
}

const TONE_CLASS: Record<string, string> = {
  pending: 'rr-inbox2__ticket-dot--new',
  warning: 'rr-inbox2__ticket-dot--open',
  degraded: 'rr-inbox2__ticket-dot--pending',
  healthy: 'rr-inbox2__ticket-dot--solved',
  inactive: 'rr-inbox2__ticket-dot--off'
};

/** The linked ticket's number and status, opening the ticket. */
export function LinkedTicketChip({ ticket }: { ticket: tickets.TicketSummary }) {
  const t = useT();
  return (
    <Tooltip label={t('inbox.ticket.chipHint', { subject: ticket.subject })} position="bottom">
      <Link
        to={`/tickets/${encodeURIComponent(ticket.id)}`}
        className="rr-tickets__chip"
        data-testid="linked-ticket-chip"
        aria-label={t('inbox.ticket.chipLabel', { number: ticket.number, status: ticket.status.name })}
      >
        <span className={`rr-inbox2__ticket-dot ${TONE_CLASS[categoryTone(ticket.status.category)] ?? ''}`} aria-hidden />
        {ticket.number}
        <span className="rr-inbox2__ticket-status">{ticket.status.name}</span>
      </Link>
    </Tooltip>
  );
}
